/**
 * Teleleo scroll reveal.
 *
 * Port of the animation that LandingPageWrapper installs on the landing pages
 * of teleleo.com. Sources:
 *   frontend/src/widgets/landings/LandingPageWrapper/LandingPageWrapper.tsx
 *   frontend/src/widgets/landings/hooks/useScrollReveal.ts
 *
 * Upstream renders `className="reveal"` into the markup of each landing
 * section, so the elements are hidden by CSS from the first paint, and an
 * IntersectionObserver adds `visible` once the element enters the viewport
 * (threshold 0.15, rootMargin '0px 0px -40px 0px', unobserved afterwards, so
 * every element animates exactly once).
 *
 * Our blocks cannot carry that class: the theme has 17 tll-* blocks and none of
 * them may be edited here. So this script does both halves itself:
 *
 *   1. Synchronously, while <head> is still parsing, it injects the hidden
 *      state for the target selectors and marks <html>. That is why the rule
 *      lives here and not in teleleo-brand.css: a class added later by JS would
 *      only take effect after the first paint, and long landing pages do paint
 *      before deferred scripts run — the content would flash in, jump back to
 *      hidden and fade in again. Keeping the rule here also means the hidden
 *      state exists only when this script runs: with JS off, or on a browser
 *      that fails any of the checks below, nothing is ever hidden.
 *   2. Once the DOM is parsed it observes the same elements and adds
 *      `tll-reveal-visible`, which is what the transition in
 *      assets/teleleo-brand.css animates.
 *
 * Class names are tll-prefixed rather than upstream's generic `reveal` /
 * `visible` to stay clear of Horizon's own utility classes.
 */
(function teleleoScrollReveal() {
  /**
   * Page handles that animate.
   *
   * Upstream this is not a list but a component boundary: only pages wrapped in
   * LandingPageWrapper get the CSS, and the `.landing-page .reveal` scoping
   * makes the class inert everywhere else. The pages deliberately left out do
   * not animate on teleleo.com either — the home page, /about, /faq,
   * /how-to-use, /contact-us, /destinations and the eight country pages,
   * /media, /news, /sitemap and the legal stubs all render their sections
   * instantly there.
   *
   * The wrapper is a necessary but not a sufficient condition, so the list is
   * one entry shorter than the set of wrapped pages: teleleo.com/pricing is
   * wrapped but renders no `reveal` element at all (verified in the deployed
   * HTML — zero `class="… reveal"` on the live page), while our template builds
   * its notes section out of tll-features-grid, whose cards ARE targets. Left on
   * the list, /pages/pricing would fade in four cards that the live page simply
   * shows. Keep it off unless upstream Pricing.tsx starts revealing.
   *
   * When a page is added to the theme, check both halves against upstream: the
   * wrapper, and whether the sections our blocks port actually carry `reveal`.
   */
  const ANIMATED_PAGES = [
    'api-integration',
    'auto-dialer',
    'call-centers',
    'dashboard-features',
    'delivery',
    'mobile-proxy',
    'multi-country-offices',
    'online-business',
    'payment-terms',
    'personal-use',
    'pre-order',
    'referral-program',
    'remote-workers',
    'small-businesses',
    'sms-follow-up',
    'sms-marketing',
    'vpn',
    'who-is-it-for',
  ]

  /**
   * Elements that animate, one entry per upstream section that carries
   * `className="reveal"`. Each tll-* block is a port of one landing widget, so
   * the mapping is one to one:
   *
   *   AudienceGrid     -> header + every card
   *   ComparisonTable  -> header + the table wrapper
   *   CtaBanner        -> the inner panel
   *   FaqAccordion     -> header + the question list as a whole
   *   FeaturesGrid     -> header + every card
   *   SpecsSection     -> the panel as a whole
   *   StepsSection     -> header + every step
   *
   * Blocks whose upstream widget has no `reveal` at all are absent on purpose:
   * HeroSection, StatsBanner, ComparisonPoints and FinalCtaSection, plus the
   * blocks ported from bespoke page markup (breadcrumbs, blog preview, country
   * grid, link grid, pricing cards, prose).
   */
  const TARGETS = [
    '.tll-audience-grid__header',
    '.tll-audience-grid__card',
    '.tll-comparison-table__header',
    '.tll-comparison-table__scroll',
    '.tll-cta-banner__inner',
    '.tll-faq-accordion__header',
    '.tll-faq-accordion__list',
    '.tll-features-grid__header',
    '.tll-features-grid__card',
    '.tll-specs__panel',
    '.tll-steps__header',
    '.tll-steps__item',
  ].join(',')

  const ROOT_CLASS = 'tll-reveal-js'
  const ANIMATED_CLASS = 'tll-reveal'
  const VISIBLE_CLASS = 'tll-reveal-visible'
  const THRESHOLD = 0.15
  const ROOT_MARGIN = '0px 0px -40px 0px'
  const OFFSET = '24px'

  const root = document.documentElement

  if (!('IntersectionObserver' in window)) return

  // Both the injected rule and the queries below rely on :is(). A browser that
  // has IntersectionObserver but not :is() would throw on the first query —
  // after the content had been hidden. Fail before touching anything.
  try {
    document.querySelector(':is(a)')
  } catch (error) {
    return
  }

  const reducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null

  // Upstream animates regardless of the motion preference. Honouring it is a
  // deliberate difference: the end state is identical, only the movement is
  // dropped.
  if (reducedMotion && reducedMotion.matches) return

  const pathname = window.location.pathname
  if (pathname.indexOf('/pages/') === -1) return

  const handle = pathname.replace(/\/+$/, '').split('/').pop()
  if (ANIMATED_PAGES.indexOf(handle) === -1) return

  const style = document.createElement('style')
  style.textContent =
    'html.' +
    ROOT_CLASS +
    ' :is(' +
    TARGETS +
    '):not(.' +
    VISIBLE_CLASS +
    '){opacity:0;transform:translateY(' +
    OFFSET +
    ')}'
  document.head.appendChild(style)
  root.classList.add(ROOT_CLASS)

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        entry.target.classList.add(VISIBLE_CLASS)
        observer.unobserve(entry.target)
      }
    },
    { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
  )

  /** Drops the whole effect and leaves every element visible. */
  const abandon = () => {
    observer.disconnect()
    root.classList.remove(ROOT_CLASS)
  }

  // `:not(.tll-reveal)` is what keeps a second scan from observing an element
  // twice: the class is added the moment an element is picked up.
  const UNBOUND_QUERY = ':is(' + TARGETS + '):not(.' + ANIMATED_CLASS + ')'

  const scan = () => {
    let nodes
    try {
      nodes = document.querySelectorAll(UNBOUND_QUERY)
    } catch (error) {
      // Nothing may stay hidden because of a failed query.
      abandon()
      return
    }

    for (const node of nodes) {
      node.classList.add(ANIMATED_CLASS)
      observer.observe(node)
    }
  }

  if (reducedMotion) {
    const onPreferenceChange = (event) => {
      if (event.matches) abandon()
    }
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onPreferenceChange)
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(onPreferenceChange)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan)
  } else {
    scan()
  }

  // Sections are re-rendered by the theme editor and by `shopify theme dev`
  // hot reload. The injected rule hides those new nodes too, so they have to be
  // observed as well. `scan` is idempotent, so the extra pass on load is free.
  document.addEventListener('shopify:section:load', scan)
  window.addEventListener('load', scan)
})()
